import { useCallback, useMemo, useRef, useState } from 'react';
import { AdviseCurrentSupplier, AdviseSupplierInvoice } from '../../../../types/advise';
import { LineField, monthlyTotalError } from './currentSupplierLimits';
import { useEngagementEditor } from './EngagementEditorContext';
import {
  blankLine,
  DraftLine,
  draftLinesError,
  draftLinesOf,
  draftTotals,
  isLinesDraftChanged,
  linesReplacement,
  withLineEdit
} from './manualSpendDraft.calc';

export type SpendEntry = 'line_items' | 'monthly_total';

const savedEntry = (supplier: AdviseCurrentSupplier): SpendEntry =>
  supplier.spend_entry === 'monthly_total' ? 'monthly_total' : 'line_items';

const savedTotal = (supplier: AdviseCurrentSupplier) => supplier.monthly_total?.toString() ?? '';

const typedTotal = (text: string) => (monthlyTotalError(text) ? 0 : Number(text));

const useDraftLines = (saved: DraftLine[]) => {
  const [lines, setLines] = useState(saved);
  const added = useRef(0);
  // Stable, so a line typed into re-renders alone however many lines there are.
  const edit = useCallback(
    (key: string, change: (line: DraftLine) => DraftLine) =>
      setLines((current) => current.map((line) => (line.key === key ? change(line) : line))),
    []
  );

  return {
    lines,
    setLines,
    editField: useCallback(
      (key: string, field: LineField, text: string) => edit(key, (line) => withLineEdit(line, field, text)),
      [edit]
    ),
    setCategory: useCallback((key: string, category: string) => edit(key, (line) => ({ ...line, category })), [edit]),
    setInScope: useCallback(
      (key: string, inScope: boolean) => edit(key, (line) => ({ ...line, in_scope: inScope })),
      [edit]
    ),
    addLine: useCallback(() => setLines((current) => [...current, blankLine(`added-${++added.current}`)]), []),
    removeLine: useCallback((key: string) => setLines((current) => current.filter((line) => line.key !== key)), [])
  };
};

const useEntryDraft = (supplier: AdviseCurrentSupplier) => {
  const [entry, setEntry] = useState(savedEntry(supplier));
  const [total, setTotal] = useState(savedTotal(supplier));
  return {
    entry,
    setEntry,
    total,
    setTotal,
    switched: entry !== savedEntry(supplier),
    totalChanged: monthlyTotalError(total) ? total !== savedTotal(supplier) : Number(total) !== supplier.monthly_total,
    resetEntry: () => {
      setEntry(savedEntry(supplier));
      setTotal(savedTotal(supplier));
    }
  };
};

// Reset in place rather than remounted, so the contract details beside the draft keep what is being typed.
const useResetWhenSaved = (savedKey: string, reset: () => void) => {
  const [syncedKey, setSyncedKey] = useState(savedKey);
  if (savedKey === syncedKey) return;
  setSyncedKey(savedKey);
  reset();
};

export const useManualSpendDraft = (supplier: AdviseCurrentSupplier, invoice: AdviseSupplierInvoice | null) => {
  const { currentSuppliers } = useEngagementEditor();
  const savedLines = invoice ? invoice.line_items : supplier.line_items;
  const version = invoice ? invoice.line_items_version : supplier.line_items_version;
  const draft = useDraftLines(draftLinesOf(savedLines));
  const { resetEntry, switched, totalChanged, ...entry } = useEntryDraft(supplier);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    draft.setLines(draftLinesOf(savedLines));
    resetEntry();
    setChecked(false);
  };

  useResetWhenSaved([version, supplier.spend_entry, supplier.monthly_total].join('|'), reset);

  const onTotal = entry.entry === 'monthly_total';
  const linesChanged = useMemo(() => isLinesDraftChanged(savedLines, draft.lines), [savedLines, draft.lines]);
  const linesError = useMemo(() => draftLinesError(draft.lines), [draft.lines]);
  const linesTotals = useMemo(() => draftTotals(draft.lines), [draft.lines]);
  const changed = switched || (onTotal ? totalChanged : linesChanged);
  const error = onTotal ? monthlyTotalError(entry.total) : linesError;

  const send = () =>
    onTotal
      ? currentSuppliers?.saveMonthlyTotal(supplier.id, Number(entry.total))
      : currentSuppliers?.saveManualLines(supplier.id, invoice?.id ?? null, linesReplacement(draft.lines, version));

  return {
    ...draft,
    ...entry,
    changed,
    checked,
    error: checked ? error : null,
    saving,
    totals: onTotal ? { inScope: typedTotal(entry.total), outOfScope: 0 } : linesTotals,
    cancel: reset,
    save: async () => {
      setChecked(true);
      if (error) return;
      setSaving(true);
      try {
        await send();
      } finally {
        setSaving(false);
      }
    }
  };
};

export type ManualSpendDraft = ReturnType<typeof useManualSpendDraft>;
